import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { api, CATEGORIES, API } from "../lib/api";
import { fetchRecommendations } from "../lib/recommendations";
import ProductCard from "../components/ProductCard";
import ConsultoraModal from "../components/ConsultoraModal";

const FALLBACK = {
  hero: "https://images.unsplash.com/photo-1611652022419-a9419f74343d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  tryon_promo: "https://images.unsplash.com/photo-1633934542430-0905ccb5f050?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  story: "https://images.unsplash.com/photo-1617191880362-aac615de3c26?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  cat_colares: "https://images.unsplash.com/photo-1611652022419-a9419f74343d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  cat_aneis: "https://images.unsplash.com/photo-1686807252464-a2e9ee06b2cf?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  cat_brincos: "https://images.unsplash.com/photo-1601121141461-920cb1993441?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  cat_pingentes: "https://images.unsplash.com/photo-1633934542430-0905ccb5f050?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  cat_pulseiras: "https://images.unsplash.com/photo-1617191880362-aac615de3c26?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  cat_outros: "https://images.unsplash.com/photo-1620656798579-1984d9e87df7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
};

const CAT_KEY = {
  "Colares": "cat_colares",
  "Anéis": "cat_aneis",
  "Brincos": "cat_brincos",
  "Pingentes": "cat_pingentes",
  "Pulseiras": "cat_pulseiras",
  "Outros": "cat_outros",
};

// Cache homepage settings in localStorage so the hero image renders instantly on
// every reload (no 3-second flash of the Unsplash fallback while waiting for the
// API round-trip). On first-ever visit there's no cache → we keep imgs/hero as
// null and skip rendering the fallback Unsplash image to avoid the "wrong image
// flashing" effect; the real image fades in once the API responds.
const CACHE_KEY = "prize_home_settings_v1";
const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
const writeCache = (data) => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch { /* ignore */ }
};

export default function HomePage() {
  const cached = readCache();
  const [novidades, setNovidades] = useState([]);
  const [hero, setHero] = useState(cached?.hero ?? null);
  const [imgs, setImgs] = useState(cached?.imgs ?? null);
  const [consultoraOpen, setConsultoraOpen] = useState(false);
  // Fresh diversification on each mount → revisiting "/" shows a different mix
  // while staying stable for the lifetime of THIS render (no flicker).
  const diversifySeed = useMemo(() => Date.now() ^ Math.floor(Math.random() * 1e9), []);

  useEffect(() => {
    // "New In" home strip: when the affinity flag is ON and the visitor is
    // logged in with enough signals, the backend mixes affinity-scored picks
    // with diversified ones. Otherwise it transparently returns a diversified
    // selection (= current behaviour, no visual change for logged-out users).
    fetchRecommendations({
      context: "home",
      limit: 8,
      localPool: async () => {
        const r = await api.get("/products", { params: { is_new: true, limit: 200 } });
        return r.data || [];
      },
      diversifyOpts: { seed: diversifySeed },
    })
      .then(({ items }) => setNovidades(items))
      .catch(() => {});
    Promise.all([
      api.get("/settings/hero").catch(() => null),
      api.get("/settings/homepage-images").catch(() => null),
    ]).then(([heroResp, imgsResp]) => {
      const nextHero = heroResp?.data ?? null;
      const nextImgs = imgsResp?.data?.images ?? {};
      if (nextHero) setHero(nextHero);
      setImgs(nextImgs);
      writeCache({ hero: nextHero, imgs: nextImgs });
    });
  }, [diversifySeed]);

  // Resolves a homepage image path. Returns:
  //  - the configured URL, when available;
  //  - the Unsplash fallback, when settings have loaded but no image is configured for this slot;
  //  - null while settings are still loading (used to skip rendering the fallback flash).
  const img = (key) => {
    if (imgs === null) return null;
    const path = imgs[key];
    if (path) return `${API}/files/${path}`;
    return FALLBACK[key];
  };

  const heroImg = hero?.image_url || img("hero");

  return (
    <div className="bg-white" data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[88vh] min-h-[600px] overflow-hidden bg-[#1A1A1A]" data-testid="hero-section">
        {heroImg && (
          <img
            src={heroImg}
            alt="Prize Joias"
            className="absolute inset-0 w-full h-full object-cover"
            fetchPriority="high"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50" />
        <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-8 flex flex-col justify-end pb-20 md:pb-28">
          <p className="overline text-[#B8A88A] mb-5 fade-in">{hero?.supertitle || "Coleção Atemporal"}</p>
          <h1 className="font-display text-white text-5xl md:text-7xl lg:text-8xl font-light leading-[1.05] max-w-3xl mb-6 fade-in">
            {hero?.title || "Joias únicas para momentos únicos"}
          </h1>
          <p className="font-italic-display text-white/90 text-xl md:text-2xl max-w-xl mb-10 fade-in">
            {hero?.subtitle || "Curadoria de peças raras das marcas mais desejadas do mundo."}
          </p>
          <div className="fade-in">
            <Link to="/joias/todos" data-testid="hero-cta" className="btn-outline-light">
              {hero?.cta_text || "Explorar Coleção"}
            </Link>
          </div>
        </div>
      </section>

      {/* BRANDS */}
      <section className="bg-[#FAF9F7] py-14" data-testid="brands-strip">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-[#6B6B6B] mb-6 font-italic-display text-lg">
            Joias selecionadas das marcas mais desejadas do mundo
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 md:gap-x-16 gap-y-3">
            {["Cartier", "Tiffany & Co.", "Bulgari", "Vivara", "H.Stern"].map((b) => (
              <span key={b} className="font-display text-xl md:text-2xl text-[#1A1A1A]/80 tracking-wide">
                {b}
              </span>
            ))}
            <span className="font-display text-xl md:text-2xl text-[#1A1A1A]/80 tracking-wide italic">
              &amp; mais
            </span>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-24" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="overline text-[#A8A8A8] mb-4">Nossa Curadoria</p>
            <h2 className="font-display text-4xl md:text-5xl">Explore por categoria</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/joias/${encodeURIComponent(c.slug)}`}
                data-testid={`category-${c.slug}`}
                className="relative aspect-[3/4] overflow-hidden group block"
              >
                <img src={img(CAT_KEY[c.slug])} alt={c.label} className="w-full h-full object-cover img-hover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 text-center">
                  <h3 className="font-display text-white text-3xl md:text-4xl">{c.label}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TRY ON HIGHLIGHT */}
      <section className="bg-[#FAF9F7] py-24" data-testid="tryon-highlight">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div className="order-2 md:order-1">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={16} className="text-[#B8A88A]" />
              <p className="overline text-[#B8A88A]">Try On Experience</p>
            </div>
            <h2 className="font-display text-4xl md:text-5xl mb-6 leading-tight">
              Experimente antes<br/>de se apaixonar.
            </h2>
            <p className="text-[#6B6B6B] text-base leading-relaxed mb-3 max-w-md">
              Nossa IA exclusiva coloca qualquer peça em você — em segundos.
              E uma consultora virtual te conta exatamente por que ela combina.
            </p>
            <p className="font-italic-display text-lg text-[#8C7B5E] mb-10 max-w-md">
              "É como provar na loja, sem sair de casa."
            </p>
            <Link to="/try-on" data-testid="tryon-cta" className="btn-accent">
              Experimentar agora
              <ArrowRight size={14} className="ml-2" />
            </Link>
          </div>
          <div className="order-1 md:order-2 aspect-[4/5] overflow-hidden">
            <img src={img("tryon_promo")} alt="Try On" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="py-24" data-testid="new-arrivals">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="overline text-[#A8A8A8] mb-4">New In</p>
              <h2 className="font-display text-4xl md:text-5xl">Acabaram de chegar</h2>
            </div>
            <Link to="/novidades" className="hidden md:inline-flex overline text-[#1A1A1A] hover:text-[#B8A88A]">
              Ver todas →
            </Link>
          </div>
          {novidades.length === 0 ? (
            <p className="text-[#A8A8A8] italic">Em breve, novas peças.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
              {novidades.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
          <div className="text-center mt-12 md:hidden">
            <Link to="/novidades" className="btn-secondary">Ver todas as novidades</Link>
          </div>
        </div>
      </section>

      {/* CONSULTORA PERSONALIZADA — convite discreto entre Novidades e Story */}
      <section className="py-16 md:py-20 bg-[#FAF9F7] border-y border-[#E8E4DF]" data-testid="consultora-strip">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="overline text-[#B8A88A] mb-4">Atendimento personalizado</p>
          <h2 className="font-display text-3xl md:text-4xl mb-4 leading-tight">
            Precisa de ajuda para escolher a joia perfeita?
          </h2>
          <p className="text-[#6B6B6B] mb-8 leading-relaxed max-w-xl mx-auto">
            Nossas consultoras te acompanham por WhatsApp, sem pressa e sem compromisso —
            entendendo o estilo, a ocasião e o orçamento para sugerir as melhores opções do catálogo.
          </p>
          <button
            onClick={() => setConsultoraOpen(true)}
            className="btn-primary"
            data-testid="home-consultora-cta"
          >
            Falar com uma consultora
          </button>
        </div>
      </section>

      {/* STORY */}
      <section className="py-24 bg-[#1A1A1A] text-white" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div className="aspect-[4/5] overflow-hidden">
            <img src={img("story")} alt="Nossa história" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="overline text-[#B8A88A] mb-6">Nossa História</p>
            <h2 className="font-display text-4xl md:text-5xl mb-8 leading-tight">
              Curadoria feita com<br/>amor e critério.
            </h2>
            <p className="text-white/70 mb-5 leading-relaxed">
              Há duas gerações, nossa família percorre o mundo em busca das peças mais raras.
              Cada joia em nosso catálogo passou pelos olhos atentos de quem aprendeu a reconhecer
              brilho, autenticidade e história.
            </p>
            <p className="text-white/70 mb-10 leading-relaxed">
              <span className="font-italic-display text-[#B8A88A]">Uma vez vendida, nunca mais disponível.</span>{" "}
              Por isso, quem leva uma Prize Joias, leva também a certeza de carregar algo único.
            </p>
            <Link to="/sobre" className="btn-outline-light">Conheça nossa história</Link>
          </div>
        </div>
      </section>

      {/* GIFT GUIDE TEASER */}
      <section className="bg-[#FAF9F7] py-24" data-testid="gift-teaser">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="overline text-[#A8A8A8] mb-4">Guia de Presentes</p>
          <h2 className="font-display text-4xl md:text-5xl mb-3">Presente perfeito existe.</h2>
          <p className="font-italic-display text-[#6B6B6B] text-xl mb-16">É só saber onde procurar.</p>
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { title: "Pelo Investimento", subtitle: "Delicado e especial", filter: "0-500" },
              { title: "Para o casamento", subtitle: "Símbolos para a vida", filter: "casamento" },
              { title: "Para o Noivado", subtitle: "A melhor surpresa", filter: "para" },
            ].map((c) => (
              <Link key={c.title} to="/presentes" className="aspect-[5/3] bg-white relative overflow-hidden flex flex-col items-center justify-center group">
                <div className="absolute inset-0 border border-[#E8E4DF] group-hover:border-[#B8A88A] transition-colors" />
                <h3 className="font-display text-3xl mb-2">{c.title}</h3>
                <p className="text-sm text-[#6B6B6B]">{c.subtitle}</p>
              </Link>
            ))}
          </div>
          <Link to="/presentes" className="btn-primary">Explorar guia de presentes</Link>
        </div>
      </section>

      <ConsultoraModal open={consultoraOpen} onOpenChange={setConsultoraOpen} />
    </div>
  );
}
